import { useState } from "react";
import {
  Sun,
  Moon,
  Type,
  ALargeSmall,
  ScrollText,
  GalleryHorizontalEnd,
  ArrowRight,
  ArrowLeft,
  Wrench,
  Sparkles,
  Check,
} from "lucide-react";
import { useApp } from "../context/AppContext";

/**
 * First-time setup wizard. Renders fullscreen inside the MobileFrame.
 * Shown when user is logged in but setupCompleted === false.
 */
export default function OnboardingWizard() {
  const {
    theme,
    setTheme,
    fontSize,
    setFontSize,
    formLayout,
    setFormLayout,
    completeSetup,
    user,
  } = useApp();
  const [step, setStep] = useState(0);

  const steps = [
    { id: "welcome", label: "Bienvenida" },
    { id: "theme", label: "Tema" },
    { id: "font", label: "Tipografía" },
    { id: "layout", label: "Formularios" },
    { id: "done", label: "Listo" },
  ];

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const finish = () => completeSetup();

  return (
    <div
      className="absolute inset-0 z-[55] bg-white dark:bg-zinc-950 flex flex-col"
      data-testid="onboarding-wizard"
    >
      {/* Top hazard stripe + progress */}
      <div className="h-2 stripes-warning shrink-0" />
      <div className="px-5 pt-4 pb-3 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
            // Configuración inicial · {step + 1}/{steps.length}
          </span>
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
            {steps[step].label}
          </span>
        </div>
        <div className="flex gap-1">
          {steps.map((s, i) => (
            <div
              key={s.id}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step
                  ? "bg-orange-500"
                  : "bg-zinc-200 dark:bg-zinc-800"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar px-6 pb-4">
        {step === 0 && (
          <StepWelcome name={user?.name} />
        )}
        {step === 1 && (
          <StepTheme value={theme} onChange={setTheme} />
        )}
        {step === 2 && (
          <StepFont value={fontSize} onChange={setFontSize} />
        )}
        {step === 3 && (
          <StepLayout value={formLayout} onChange={setFormLayout} />
        )}
        {step === 4 && (
          <StepDone
            theme={theme}
            fontSize={fontSize}
            formLayout={formLayout}
          />
        )}
      </div>

      <div className="border-t border-zinc-200 dark:border-zinc-800 p-4 shrink-0 flex gap-2">
        {step > 0 && step < steps.length - 1 && (
          <button
            onClick={back}
            data-testid="onboarding-back-btn"
            className="h-12 px-4 rounded-sm bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white font-mono-tech text-xs uppercase tracking-widest border border-zinc-200 dark:border-zinc-700 transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Atrás
          </button>
        )}
        {step === 0 && (
          <button
            onClick={() => {
              completeSetup();
            }}
            data-testid="onboarding-skip-btn"
            className="h-12 px-4 rounded-sm bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 font-mono-tech text-xs uppercase tracking-widest border border-zinc-200 dark:border-zinc-700 transition-colors"
          >
            Omitir
          </button>
        )}

        {step < steps.length - 1 ? (
          <button
            onClick={next}
            data-testid="onboarding-next-btn"
            className="flex-1 group bg-orange-500 hover:bg-orange-600 text-white font-display font-bold uppercase tracking-widest h-12 rounded-sm transition-all glow-orange flex items-center justify-center gap-2"
          >
            {step === 0 ? "Configurar" : "Siguiente"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        ) : (
          <button
            onClick={finish}
            data-testid="onboarding-finish-btn"
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-display font-bold uppercase tracking-widest h-12 rounded-sm transition-all glow-orange flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            Empezar a inspeccionar
          </button>
        )}
      </div>
    </div>
  );
}

function StepWelcome({ name }) {
  return (
    <div className="flex flex-col h-full justify-center text-center">
      <div className="w-16 h-16 mx-auto mb-5 bg-orange-500 rounded-sm flex items-center justify-center shadow-[0_0_24px_rgba(249,115,22,0.5)]">
        <Wrench className="w-8 h-8 text-white" strokeWidth={2.5} />
      </div>
      <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">
        // Engine Inspect
      </p>
      <h1 className="font-display font-bold text-3xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-3">
        Hola{name ? `, ${name}` : ""}
      </h1>
      <p className="font-mono-tech text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed max-w-[320px] mx-auto">
        Vamos a personalizar el terminal para tu taller. Solo 3 ajustes rápidos
        y empezamos. Podrás cambiarlos cuando quieras desde Ajustes.
      </p>
      <div className="mt-6 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-sm bg-orange-500/10 border border-orange-500/30 mx-auto">
        <Sparkles className="w-3 h-3 text-orange-500" />
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-orange-500">
          Solo lo verás esta vez
        </span>
      </div>
    </div>
  );
}

function ChoiceCard({ active, onClick, icon: Icon, title, description, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 p-4 rounded-md border transition-colors text-left ${
        active
          ? "border-orange-500 bg-orange-500/5 dark:bg-orange-500/10"
          : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700"
      }`}
    >
      <div
        className={`w-12 h-12 rounded-sm flex items-center justify-center shrink-0 ${
          active
            ? "bg-orange-500 text-white"
            : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300"
        }`}
      >
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-sm uppercase tracking-wider text-zinc-900 dark:text-white">
          {title}
        </p>
        <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">
          {description}
        </p>
      </div>
      <div
        className={`w-4 h-4 rounded-full border-2 shrink-0 transition-colors ${
          active
            ? "border-orange-500 bg-orange-500"
            : "border-zinc-300 dark:border-zinc-700"
        }`}
      />
    </button>
  );
}

function StepTheme({ value, onChange }) {
  return (
    <div className="pt-4">
      <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        // Paso 1 de 3
      </p>
      <h2 className="font-display font-bold text-2xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-2">
        Tema visual
      </h2>
      <p className="font-mono-tech text-xs text-zinc-700 dark:text-zinc-300 mb-5">
        Elige el modo que mejor se adapte a la luz del taller.
      </p>
      <div className="space-y-2.5">
        <ChoiceCard
          active={value === "light"}
          onClick={() => onChange("light")}
          icon={Sun}
          title="Modo Claro"
          description="Taller iluminado · Día"
          testid="onboarding-theme-light"
        />
        <ChoiceCard
          active={value === "dark"}
          onClick={() => onChange("dark")}
          icon={Moon}
          title="Modo Oscuro"
          description="Bajo brillo · Garaje nocturno"
          testid="onboarding-theme-dark"
        />
      </div>
    </div>
  );
}

function StepFont({ value, onChange }) {
  return (
    <div className="pt-4">
      <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        // Paso 2 de 3
      </p>
      <h2 className="font-display font-bold text-2xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-2">
        Tamaño de letra
      </h2>
      <p className="font-mono-tech text-xs text-zinc-700 dark:text-zinc-300 mb-5">
        ¿Trabajas con guantes, gafas de seguridad o el móvil lejos? Elige
        letras grandes.
      </p>
      <div className="space-y-2.5">
        <ChoiceCard
          active={value === "normal"}
          onClick={() => onChange("normal")}
          icon={Type}
          title="Normal"
          description="Densidad estándar"
          testid="onboarding-font-normal"
        />
        <ChoiceCard
          active={value === "large"}
          onClick={() => onChange("large")}
          icon={ALargeSmall}
          title="Muy Grande"
          description="Mejor legibilidad en taller"
          testid="onboarding-font-large"
        />
      </div>
    </div>
  );
}

function StepLayout({ value, onChange }) {
  return (
    <div className="pt-4">
      <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        // Paso 3 de 3
      </p>
      <h2 className="font-display font-bold text-2xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-2">
        Tipo de checklist
      </h2>
      <p className="font-mono-tech text-xs text-zinc-700 dark:text-zinc-300 mb-5">
        ¿Cómo prefieres rellenar las inspecciones?
      </p>
      <div className="space-y-2.5">
        <ChoiceCard
          active={value === "single"}
          onClick={() => onChange("single")}
          icon={ScrollText}
          title="Una página"
          description="Todas las preguntas a la vez"
          testid="onboarding-layout-single"
        />
        <ChoiceCard
          active={value === "slides"}
          onClick={() => onChange("slides")}
          icon={GalleryHorizontalEnd}
          title="Modo diapositiva"
          description="Pregunta a pregunta · Foco total"
          testid="onboarding-layout-slides"
        />
      </div>
    </div>
  );
}

function StepDone({ theme, fontSize, formLayout }) {
  const summary = [
    { label: "Tema", value: theme === "dark" ? "Oscuro" : "Claro" },
    { label: "Tipografía", value: fontSize === "large" ? "Muy grande" : "Normal" },
    { label: "Checklist", value: formLayout === "slides" ? "Diapositiva" : "Una página" },
  ];
  return (
    <div className="flex flex-col h-full justify-center text-center">
      <div className="w-16 h-16 mx-auto mb-5 bg-green-500 rounded-sm flex items-center justify-center shadow-[0_0_24px_rgba(34,197,94,0.45)]">
        <Check className="w-8 h-8 text-white" strokeWidth={3} />
      </div>
      <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-green-600 dark:text-green-500 mb-2">
        // Configuración guardada
      </p>
      <h2 className="font-display font-bold text-3xl uppercase leading-tight tracking-tight text-zinc-900 dark:text-white mb-5">
        Todo listo
      </h2>
      <div className="bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-4 max-w-[320px] mx-auto w-full">
        <ul className="space-y-2 text-left">
          {summary.map((s) => (
            <li
              key={s.label}
              className="flex items-center justify-between font-mono-tech text-xs"
            >
              <span className="text-zinc-500 uppercase tracking-widest text-[10px]">
                {s.label}
              </span>
              <span className="text-zinc-900 dark:text-white font-bold uppercase">
                {s.value}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
